import { useCallback } from "react";

import { type FetchResult, type MutationFunctionOptions, useMutation } from "@apollo/client";

import { type ApolloResult } from "../../shared/models";
import {
    type UpdateLensMutation,
    type UpdateLensMutationVariables,
    updateLens as updateLensMutation,
} from "../api/mutations";
import { getLens } from "../api/queries";

type UpdateLensOptions = Omit<MutationFunctionOptions<UpdateLensMutation, UpdateLensMutationVariables>, "variables"> & {
    // Variables is required. Must specify fields to update
    variables: UpdateLensMutationVariables;
};

export const useUpdateLens = (): ApolloResult<{
    updateLens: (options: UpdateLensOptions) => Promise<FetchResult<UpdateLensMutation>>;
}> => {
    const [executeUpdateLens, { loading, error }] = useMutation<UpdateLensMutation, UpdateLensMutationVariables>(
        updateLensMutation,
    );

    const updateLens = useCallback(
        async (options: UpdateLensOptions) => {
            if (options.variables.lensId === "") {
                throw new Error("Cannot update lens: lensId is required");
            }

            const additionalRefetchQueries = [{ query: getLens, variables: { lensId: options.variables.lensId } }];

            return executeUpdateLens({
                ...options,
                refetchQueries: (result) => {
                    // Get existing queries, resolve functions, default to empty array
                    const existing = options.refetchQueries;
                    const resolved = typeof existing === "function" ? existing(result) : existing;
                    const existingArray = Array.isArray(resolved) ? resolved : [];

                    // Always include lens refetch
                    return [...existingArray, ...additionalRefetchQueries];
                },
            });
        },
        [executeUpdateLens],
    );
    return { updateLens, loading, error };
};
